import { useCallback, useEffect, useRef } from 'react'
import L from 'leaflet'
import { useTranslation } from 'react-i18next'
import { Button } from '@clouway/ui'
import { Maximize } from 'lucide-react'
import 'leaflet/dist/leaflet.css'

import { markerKind, toPositions } from './route'
import type { RoutePoint } from '../types'

// Default center: Bulgaria (plan.md §3.6).
const BULGARIA: L.LatLngTuple = [42.5205, 25.4113]

function markerIcon(kind: ReturnType<typeof markerKind>, heading: number): L.DivIcon {
  if (kind === 'start' || kind === 'end') {
    const color = kind === 'start' ? '#16a34a' : '#dc2626'
    return L.divIcon({
      className: '',
      iconSize: [16, 16],
      iconAnchor: [8, 8],
      html: `<span style="display:block;width:16px;height:16px;border-radius:9999px;background:${color};border:2px solid white;box-shadow:0 0 0 1px rgba(0,0,0,.3)"></span>`,
    })
  }
  // Waypoint: an arrow rotated to the recorded heading.
  return L.divIcon({
    className: '',
    iconSize: [14, 14],
    iconAnchor: [7, 7],
    html: `<span style="display:block;transform:rotate(${heading}deg);color:#334155">▲</span>`,
  })
}

export function StatusMap({ route, onSelect }: { route: RoutePoint[]; onSelect: (point: RoutePoint) => void }) {
  const { t } = useTranslation('feature')
  const elRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<L.Map | null>(null)
  const layerRef = useRef<L.LayerGroup | null>(null)
  const positionsRef = useRef<[number, number][]>([])

  useEffect(() => {
    if (!elRef.current || mapRef.current) return
    const map = L.map(elRef.current, { center: BULGARIA, zoom: 7 })
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
      maxZoom: 19,
    }).addTo(map)
    layerRef.current = L.layerGroup().addTo(map)
    mapRef.current = map
    return () => {
      map.remove()
      mapRef.current = null
      layerRef.current = null
    }
  }, [])

  useEffect(() => {
    const map = mapRef.current
    const layer = layerRef.current
    if (!map || !layer) return
    layer.clearLayers()

    const positions = toPositions(route)
    positionsRef.current = positions
    if (positions.length === 0) return

    L.polyline(positions, { color: '#16a34a', weight: 3 }).addTo(layer)
    route.forEach((point, index) => {
      L.marker([point.lat, point.long], { icon: markerIcon(markerKind(index, route.length), point.heading) })
        .on('click', () => onSelect(point))
        .addTo(layer)
    })
    map.fitBounds(positions, { padding: [30, 30] })
  }, [route, onSelect])

  const fit = useCallback(() => {
    if (mapRef.current && positionsRef.current.length > 0) {
      mapRef.current.fitBounds(positionsRef.current, { padding: [30, 30] })
    }
  }, [])

  return (
    <div className="relative">
      <div ref={elRef} className="h-[60vh] w-full rounded-md" />
      {route.length > 0 && (
        <Button
          size="icon"
          variant="outline"
          className="absolute right-3 top-3 z-[400] bg-background"
          aria-label={t('trackingObjects.status.statusAt')}
          onClick={fit}
        >
          <Maximize className="size-4" />
        </Button>
      )}
    </div>
  )
}
